import { logger } from '@/src/logging/logger';

const storageKeyPrefix = 'sparkone.voice.audio-device-preferences';
const audioDevicePreferencesVersion = 1;
const maximumDeviceIdLength = 512;
const maximumScopeIdLength = 256;

export type AudioDevicePreferences = {
  version: typeof audioDevicePreferencesVersion;
  inputDeviceId: string;
  outputDeviceId: string;
};

/** Browser-local preferences are isolated by the authenticated tenant and user. */
export type AudioDevicePreferencesScope = {
  tenantId: string;
  userId: string;
};

/**
 * The only error contract for browser audio-preference operations. Codes are
 * intentionally stable and messages contain no browser, device, or profile data.
 */
export type AudioPreferenceErrorCode =
  | 'AUDIO_PREFERENCES_NOT_OWNER'
  | 'AUDIO_PREFERENCES_RUNTIME_UNAVAILABLE'
  | 'AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE'
  | 'AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED'
  | 'AUDIO_PREFERENCES_SPEAKER_UNSUPPORTED'
  | 'AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED'
  | 'AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE'
  | 'AUDIO_PREFERENCES_SELECTION_REMOVED'
  | 'AUDIO_PREFERENCES_OPERATION_SUPERSEDED'
  | 'AUDIO_PREFERENCES_STORAGE_UNAVAILABLE'
  | 'AUDIO_PREFERENCES_INVALID_PREFERENCES';

const errorMessages: Record<AudioPreferenceErrorCode, string> = {
  AUDIO_PREFERENCES_NOT_OWNER: 'Audio settings are active in another SparkOne tab.',
  AUDIO_PREFERENCES_RUNTIME_UNAVAILABLE: 'Browser call audio is not available yet.',
  AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE: 'Browser microphone is not available.',
  AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED: 'Microphone selection could not be changed. Your previous microphone is still in use.',
  AUDIO_PREFERENCES_SPEAKER_UNSUPPORTED: 'This browser uses the system speaker and does not support selecting an output device.',
  AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED: 'Speaker selection could not be changed. Your previous speaker is still in use.',
  AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE: 'Audio device settings could not be refreshed. Try again.',
  AUDIO_PREFERENCES_SELECTION_REMOVED: 'The selected audio device is no longer available.',
  AUDIO_PREFERENCES_OPERATION_SUPERSEDED: 'Browser audio selection is no longer current. Choose a device again.',
  AUDIO_PREFERENCES_STORAGE_UNAVAILABLE: 'Browser audio preferences could not be saved.',
  AUDIO_PREFERENCES_INVALID_PREFERENCES: 'Browser audio preferences are unavailable.',
};

export class AudioPreferenceError extends Error {
  readonly code: AudioPreferenceErrorCode;

  constructor(code: AudioPreferenceErrorCode) {
    super(errorMessages[code]);
    this.name = 'AudioPreferenceError';
    this.code = code;
  }
}

function isBoundedIdentifier(value: unknown, maximumLength: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= maximumLength;
}

function isBoundedDeviceId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= maximumDeviceIdLength;
}

function logPreferenceFailure(failureCategory: string): void {
  logger.warn('sparkone.browser_voice.audio_device_preferences_failed', { failureCategory });
}

function preferenceError(code: AudioPreferenceErrorCode, failureCategory: string): AudioPreferenceError {
  logPreferenceFailure(failureCategory);
  return new AudioPreferenceError(code);
}

function assertScope(scope: AudioDevicePreferencesScope): void {
  if (
    !isBoundedIdentifier(scope?.tenantId, maximumScopeIdLength)
    || !isBoundedIdentifier(scope?.userId, maximumScopeIdLength)
  ) {
    throw preferenceError('AUDIO_PREFERENCES_INVALID_PREFERENCES', 'invalid_scope');
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isAudioDevicePreferences(value: unknown): value is AudioDevicePreferences {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 3 || !keys.every((key) => ['version', 'inputDeviceId', 'outputDeviceId'].includes(key))) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(value, 'version')
    && Object.prototype.hasOwnProperty.call(value, 'inputDeviceId')
    && Object.prototype.hasOwnProperty.call(value, 'outputDeviceId')
    && value.version === audioDevicePreferencesVersion
    && isBoundedDeviceId(value.inputDeviceId)
    && isBoundedDeviceId(value.outputDeviceId);
}

/** Builds an opaque, tenant-and-user-scoped browser storage key. */
export function getAudioDevicePreferencesStorageKey(scope: AudioDevicePreferencesScope): string {
  assertScope(scope);
  return `${storageKeyPrefix}.v${audioDevicePreferencesVersion}.${encodeURIComponent(scope.tenantId)}.${encodeURIComponent(scope.userId)}`;
}

export function createAudioDevicePreferences(inputDeviceId: string, outputDeviceId: string): AudioDevicePreferences {
  const preferences = { version: audioDevicePreferencesVersion, inputDeviceId, outputDeviceId } as const;
  if (!isAudioDevicePreferences(preferences)) {
    throw preferenceError('AUDIO_PREFERENCES_INVALID_PREFERENCES', 'invalid_preferences');
  }
  return preferences;
}

/** Reads safe browser-local state. It never asks the browser for media permission. */
export function readAudioDevicePreferences(
  scope: AudioDevicePreferencesScope,
  storage: Storage = window.localStorage,
): AudioDevicePreferences | null {
  const key = getAudioDevicePreferencesStorageKey(scope);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    logPreferenceFailure('storage_read_failed');
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isAudioDevicePreferences(parsed)) return parsed;
  } catch {
    // Deliberately handled as malformed state below without logging raw data.
  }

  logPreferenceFailure('invalid_stored_preferences');
  try {
    storage.removeItem(key);
  } catch {
    logPreferenceFailure('storage_remove_failed');
  }
  return null;
}

/** Writes only a confirmed, validated browser-local route. */
export function writeAudioDevicePreferences(
  scope: AudioDevicePreferencesScope,
  preferences: AudioDevicePreferences,
  storage: Storage = window.localStorage,
): void {
  const key = getAudioDevicePreferencesStorageKey(scope);
  if (!isAudioDevicePreferences(preferences)) {
    throw preferenceError('AUDIO_PREFERENCES_INVALID_PREFERENCES', 'invalid_preferences');
  }
  try {
    storage.setItem(key, JSON.stringify(preferences));
  } catch {
    throw preferenceError('AUDIO_PREFERENCES_STORAGE_UNAVAILABLE', 'storage_write_failed');
  }
}

export const audioDevicePreferencesStorageVersion = audioDevicePreferencesVersion;
